import { useEffect, useSyncExternalStore } from 'react';
import type { ActivityTrace } from './activity-trace';
import type { ToolOutcome } from './tool-presentation';

const records = new Map<string, ActivityTrace>();
const listeners = new Set<() => void>();
const started = new Map<string, number>();
const MAX_TIMINGS = 240;
const keyFor = (threadId: string, value: string) => `${threadId}:${value}`;
type ActivityScope = { ownerId: string; companyId: string; threadId: string };
let activeScope: ActivityScope | null = null;
const terminalOutcomes = new Set<ToolOutcome>(['complete', 'error', 'cancelled', 'incomplete']);
const terminalPriority: Record<ToolOutcome, number> = {
  running: 0,
  complete: 1,
  cancelled: 2,
  incomplete: 2,
  error: 3,
  approval: 0,
};

function mergeOutcome(previous: ToolOutcome | undefined, next: ToolOutcome): ToolOutcome {
  if (!previous || !terminalOutcomes.has(previous)) return next;
  if (!terminalOutcomes.has(next)) return previous;

  return terminalPriority[previous] >= terminalPriority[next] ? previous : next;
}

function traceOutcome(steps: ActivityTrace['steps']): ToolOutcome {
  return (
    (['approval', 'running', 'error', 'incomplete', 'cancelled'] as const).find((outcome) =>
      steps.some((step) => step.outcome === outcome),
    ) ?? 'complete'
  );
}

export function publishTurnActivity(threadId: string, messageId: string, snapshot: ActivityTrace) {
  const key = keyFor(threadId, messageId);
  const previous = records.get(key);
  if (previous && JSON.stringify(previous) === JSON.stringify(snapshot)) return;
  // A streamed rerender rebuilds the trace from message parts. Preserve the
  // session evidence already measured for settled steps instead of erasing it.
  if (previous) {
    const newActiveStep = snapshot.steps.some(
      (step) =>
        !previous.steps.some((candidate) => candidate.id === step.id) &&
        (step.outcome === 'running' || step.outcome === 'approval'),
    );
    const steps = snapshot.steps.map((step) => {
      const old = previous.steps.find((candidate) => candidate.id === step.id);
      const measured =
        step.durationMs === undefined && old?.durationMs !== undefined
          ? { ...step, durationMs: old.durationMs }
          : step;
      if (!old) return measured;
      const outcome = mergeOutcome(old.outcome, step.outcome);
      return outcome === step.outcome
        ? measured
        : {
            ...measured,
            outcome,
            result: old.result ?? measured.result,
            approval: old.approval ?? measured.approval,
            interrupt: old.interrupt ?? measured.interrupt,
          };
    });
    snapshot = {
      ...snapshot,
      outcome: newActiveStep
        ? traceOutcome(steps)
        : mergeOutcome(previous.outcome, traceOutcome(steps)),
      steps,
    };
  }
  records.set(key, snapshot);
  while (records.size > 120) records.delete(records.keys().next().value!);
  listeners.forEach((listener) => listener());
}

/**
 * Set the stable owner/company/thread scope from outside transient message rows.
 * A runtime remount (thread switch or sign-out) reaches this before any new
 * activity row can publish, so stale module evidence cannot leak across scopes.
 */
export function setActivityScope(scope: ActivityScope) {
  if (activeScope) {
    const accountChanged =
      activeScope.ownerId !== scope.ownerId || activeScope.companyId !== scope.companyId;
    if (accountChanged) clearAllActivity();
    else if (activeScope.threadId !== scope.threadId) clearThreadActivity(activeScope.threadId);
  }
  activeScope = scope;
}

export function ActivityScopeObserver(props: ActivityScope) {
  useEffect(() => {
    setActivityScope(props);
  }, [props.ownerId, props.companyId, props.threadId]);
  return null;
}

export function clearThreadActivity(threadId: string) {
  for (const key of records.keys()) if (key.startsWith(`${threadId}:`)) records.delete(key);
  for (const key of started.keys()) if (key.startsWith(`${threadId}:`)) started.delete(key);
  listeners.forEach((listener) => listener());
}

/** Clears mounted Activity evidence when the account/company session changes. */
export function clearAllActivity() {
  records.clear();
  started.clear();
  listeners.forEach((listener) => listener());
}

/** Session-only timing. Reloads intentionally have no duration evidence. */
export function publishToolTiming(
  threadId: string,
  messageId: string,
  toolCallId: string,
  running: boolean,
) {
  // A tool-call id is only unique within a message. Include the message id so
  // a late settlement from an older turn cannot attach timing to a new one.
  const key = keyFor(threadId, `${messageId}:${toolCallId}`);
  if (running) {
    if (!started.has(key)) started.set(key, performance.now());
    while (started.size > MAX_TIMINGS) started.delete(started.keys().next().value!);
    return;
  }
  const start = started.get(key);
  if (start === undefined) return;
  started.delete(key);
  const traceKey = keyFor(threadId, messageId);
  const trace = records.get(traceKey);
  if (!trace) return;
  const durationMs = Math.max(0, Math.round(performance.now() - start));
  const steps = trace.steps.map((step) =>
    step.id === toolCallId ? { ...step, durationMs } : step,
  );
  publishTurnActivity(threadId, messageId, { ...trace, steps });
}

/** Update a mounted activity row when an action receipt settles elsewhere. */
export function publishActionOutcome(
  threadId: string,
  messageId: string,
  toolCallId: string,
  outcome: ToolOutcome,
  result?: unknown,
) {
  const trace = records.get(keyFor(threadId, messageId));
  if (!trace) return;
  const steps = trace.steps.map((step) =>
    step.id === toolCallId
      ? { ...step, outcome: mergeOutcome(step.outcome, outcome), result: result ?? step.result }
      : step,
  );
  publishTurnActivity(threadId, messageId, { ...trace, outcome: traceOutcome(steps), steps });
}

export function useTurnActivity(threadId: string, messageId: string) {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => records.get(keyFor(threadId, messageId)) ?? null,
    () => null,
  );
}

/** Read-only snapshot for mounted integration tests and canvas adapters. */
export function getTurnActivity(threadId: string, messageId: string) {
  return records.get(keyFor(threadId, messageId)) ?? null;
}
